import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import {
  saveAgentConfig,
  getAgentConfig,
  deleteAgentConfig,
  updateAgentConfig
} from '../services/api.ts';

// Importações originais dos componentes de formulário
import AssistantIdentityForm from '../components/AssistantIdentityForm.tsx';
import ClinicInfoForm from '../components/ClinicInfoForm.tsx';
import TeamAndSpecialtiesForm from '../components/TeamAndSpecialtiesForm.tsx';
import SchedulingConfigForm from '../components/SchedulingConfigForm.tsx';
import FinancialConfigForm from '../components/FinancialConfigForm.tsx';
import ConversationFlowForm from '../components/ConversationFlowForm.tsx';
import ExistingConfigView from '../components/ExistingConfigView.tsx';
import MirrorWebhookConfig from '../components/MirrorWebhookConfig.tsx';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal.tsx';
import AgentVoiceSelector from '../components/AgentVoiceSelector.tsx';
import type { AgentVoiceSelection } from '../components/AgentVoiceSelector.tsx';
import {
  DEFAULT_MINIMUM_BOOKING_NOTICE_MINUTES,
  resolveMinimumBookingNoticeMinutes,
} from '../utils/schedulingConfig.ts';
import {
  EMPTY_URGENCY_POLICY,
  mergeSchedulingConfig,
  type UrgencyPolicyConfig,
  type UrgencyWindow,
} from '../utils/urgencyConfig.ts';

// Importações dos ícones
import {
  Bot,
  Building,
  Calendar,
  DollarSign,
  MessageSquare,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Save,
  Trash2,
  Edit2,
  CheckCircle,
  Users,
  HelpCircle,
  Pencil,
  Eye,
  Rocket,
  CircleAlert,
  CheckCircle2
} from 'lucide-react';

// Tipos
interface FewShot {
  objectionType: string;
  userMessage: string;
  botResponse: string;
}

interface Treatment {
  treatmentTitle: string; 
  description: string;
}

interface DayConfig {
  open: boolean;
  morningEnabled: boolean;
  morningStart: string;
  morningEnd: string;
  afternoonEnabled: boolean;
  afternoonStart: string;
  afternoonEnd: string;
}

interface SectionContainerProps {
  id: string;
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}

// Tipando as props específicas esperadas pelos componentes
interface ConversationFlowFormProps {
  setFewShots: (val: FewShot[]) => void;
  // Outros campos aqui
}

interface TeamAndSpecialtiesFormProps {
  setTreatments: (treatments: Treatment[]) => void;
  // Outros campos aqui
}

// Tipando explicitamente os tipos das funções esperadas pelos componentes
type SetFewShotsFunction = (val: FewShot[]) => void;
type SetTreatmentsFunction = (treatments: Treatment[]) => void;

// Helper: compute completeness % based on essential fields
function completeness(cfg: any) {
  const essentials = [
    cfg.assistantName,
    cfg.clinicName,
    cfg.clinicLocation,
    cfg.clinicWhatsApp || cfg.clinicPhoneFixed,
    cfg.step0,
  ];
  const done = essentials.filter((v) => (v ?? "").toString().trim().length > 0).length;
  return Math.round((done / essentials.length) * 100);
}

const PromptConfigRebranded: React.FC = () => {
  // Hook para tema da empresa
  const { isDark } = useTheme();
  
  // Estado para expansão das seções (funcionalidade original)
  const [expandedSections, setExpandedSections] = useState(['identity']);
  
  // Estado original
  const [hasConfig, setHasConfig] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saveStatus, setSaveStatus] = useState<null | 'saving' | 'success' | 'error'>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Toggle de expansão da seção (funcionalidade original com correção de scroll)
  const toggleSection = React.useCallback((sectionId: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Salvar posição atual do scroll
    const currentScrollY = window.scrollY;
    
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
    
    // Manter posição de scroll após a atualização
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollY);
    });
  }, []);

  // Assistant Identity
  const [assistantName, setAssistantName] = useState("");
  const [assistantRole, setAssistantRole] = useState("");
  const [assistantResponsibility, setAssistantResponsibility] = useState("");
  const [assistantFormality, setAssistantFormality] = useState("formal");
  const [assistantTone, setAssistantTone] = useState("amigável");
  const [assistantLanguage, setAssistantLanguage] = useState("Português");
  const [agentVoice, setAgentVoice] = useState<AgentVoiceSelection | null>(null);
  const [agentVoiceValid, setAgentVoiceValid] = useState(true);
  const [persistedAgentVoice, setPersistedAgentVoice] = useState<AgentVoiceSelection | null>(null);
  const [persistedAssistantName, setPersistedAssistantName] = useState('');
  const [hasUnrecognizedVoiceConfig, setHasUnrecognizedVoiceConfig] = useState(false);
  const [agentVoiceTouched, setAgentVoiceTouched] = useState(false);

  const handleAgentVoiceChange = useCallback((voice: AgentVoiceSelection | null) => {
    setAgentVoice(voice);
    setAgentVoiceTouched(true);
    setHasUnrecognizedVoiceConfig(false);
  }, []);

  // Clinic Info
  const [clinicName, setClinicName] = useState("");
  const [clinicLocation, setClinicLocation] = useState("");
  const [clinicAddress, setClinicAddress] = useState("");
  const [clinicPhoneFixed, setClinicPhoneFixed] = useState("");
  const [clinicWhatsApp, setClinicWhatsApp] = useState("");
  const [clinicMaps, setClinicMaps] = useState("");
  const [clinicInstagram, setClinicInstagram] = useState("");
  const [clinicFacebook, setClinicFacebook] = useState("");
  const [clinicSite, setClinicSite] = useState("");
  const [clinicHistory, setClinicHistory] = useState("");

  // Team and Specialties
  const [technicalResponsible, setTechnicalResponsible] = useState("");
  const [treatments, setTreatments] = useState<Treatment[]>([]);

  // Scheduling
  const initialDayConfig = {
    open: false,
    morningEnabled: false,
    morningStart: '',
    morningEnd: '',
    afternoonEnabled: false,
    afternoonStart: '',
    afternoonEnd: '',
  };

  const [monday, setMonday] = useState(initialDayConfig);
  const [tuesday, setTuesday] = useState(initialDayConfig);
  const [wednesday, setWednesday] = useState(initialDayConfig);
  const [thursday, setThursday] = useState(initialDayConfig);
  const [friday, setFriday] = useState(initialDayConfig);
  const [saturday, setSaturday] = useState(initialDayConfig);
  const [sunday, setSunday] = useState(initialDayConfig);
  const [consultationDuration, setConsultationDuration] = useState(30);
  const [numberOfSuggestions, setNumberOfSuggestions] = useState(3);
  const [minimumBookingNoticeMinutes, setMinimumBookingNoticeMinutes] = useState(
    DEFAULT_MINIMUM_BOOKING_NOTICE_MINUTES,
  );
  const [existingSchedulingConfig, setExistingSchedulingConfig] = useState<Record<string, any>>({});
  const [urgencyPolicy, setUrgencyPolicy] = useState<UrgencyPolicyConfig>(EMPTY_URGENCY_POLICY);
  const [urgencyValidationError, setUrgencyValidationError] = useState('');

  // Financial Config
  const [acceptsHealthInsurance, setAcceptsHealthInsurance] = useState(false);
  const [healthInsurancePlans, setHealthInsurancePlans] = useState("");
  const [paymentMethods, setPaymentMethods] = useState("");
  const [installmentConditions, setInstallmentConditions] = useState("");
  const [evaluationPrice, setEvaluationPrice] = useState("");
  const [treatmentPrices, setTreatmentPrices] = useState("");
  const [assessmentPrice, setAssessmentPrice] = useState("");

  // Conversation Flow
  const [step0, setStep0] = useState("");
  const [step1First, setStep1First] = useState("");
  const [step1Second, setStep1Second] = useState("");
  const [step2, setStep2] = useState("");
  const [step3, setStep3] = useState("");
  const [maxTokens, setMaxTokens] = useState(1000);

  const [financialRedirectType, setFinancialRedirectType] = useState("fixo");
  const [financialRedirectNumber, setFinancialRedirectNumber] = useState("");
  const [regularRedirectType, setRegularRedirectType] = useState("fixo");
  const [regularRedirectNumber, setRegularRedirectNumber] = useState("");
  const [maintenanceRedirectType, setMaintenanceRedirectType] = useState("fixo");
  const [maintenanceRedirectNumber, setMaintenanceRedirectNumber] = useState("");
  const [activePatientsRedirectType, setActivePatientsRedirectType] = useState("fixo");
  const [activePatientsRedirectNumber, setActivePatientsRedirectNumber] = useState("");

  const [fewShots, setFewShots] = useState([]);

  // Compute completeness percentage
  const pct = useMemo(() => completeness({
    assistantName,
    clinicName,
    clinicLocation,
    clinicWhatsApp,
    clinicPhoneFixed,
    step0
  }), [assistantName, clinicName, clinicLocation, clinicWhatsApp, clinicPhoneFixed, step0]);

  // Status color for the header (cores da empresa)
  const statusColor = hasConfig 
    ? "bg-sminy-primary/10 text-sminy-primary"
    : isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600";

  // Helper para converter dados do backend para o formato DayConfig
  const processScheduleDay = (day) => {
    return {
      open: day?.open ?? false,
      morningEnabled: day?.morningEnabled ?? false,
      morningStart: day?.morningStart ?? "",
      morningEnd: day?.morningEnd ?? "",
      afternoonEnabled: day?.afternoonEnabled ?? false,
      afternoonStart: day?.afternoonStart ?? "",
      afternoonEnd: day?.afternoonEnd ?? "",
    };
  };


  // Buscar dados do servidor ao carregar o componente
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const config = await getAgentConfig();

        if (config.assistant_identity) {
          setAssistantName(config.assistant_identity.assistant_name || "");
          setAssistantRole(config.assistant_identity.assistant_role || "");
          setAssistantResponsibility(config.assistant_identity.assistant_responsibility || "");
          setAssistantFormality(config.assistant_identity.assistant_formality || "formal");
          setAssistantTone(config.assistant_identity.assistant_tone || "amigável");
          setAssistantLanguage(config.assistant_identity.assistant_language || "Português");
        }

        const savedVoice = config.assistant_tone_and_voice?.tts;
        if (
          savedVoice?.voice_id
          && savedVoice.provider === 'elevenlabs'
          && (savedVoice.voice_gender === 'female' || savedVoice.voice_gender === 'male')
          && savedVoice.locale === 'pt-BR'
        ) {
          const hydratedVoice: AgentVoiceSelection = {
            provider: 'elevenlabs',
            voice_id: savedVoice.voice_id,
            voice_name: savedVoice.voice_name || 'Voz selecionada',
            voice_gender: savedVoice.voice_gender,
            locale: 'pt-BR',
          };
          setAgentVoice(hydratedVoice);
          setPersistedAgentVoice(hydratedVoice);
          setHasUnrecognizedVoiceConfig(false);
        } else {
          setAgentVoice(null);
          setPersistedAgentVoice(null);
          setHasUnrecognizedVoiceConfig(Boolean(savedVoice));
        }
        setAgentVoiceTouched(false);
        setPersistedAssistantName(config.assistant_identity?.assistant_name || '');

        if (config.clinic_info) {
          setClinicName(config.clinic_info.clinic_name || "");
          setClinicLocation(config.clinic_info.clinic_location || "");
          setClinicAddress(config.clinic_info.clinic_address || "");
          setClinicPhoneFixed(config.clinic_info.clinic_phone_fixed || "");
          setClinicWhatsApp(config.clinic_info.clinic_whatsapp || "");
          setClinicMaps(config.clinic_info.clinic_maps || "");
          setClinicInstagram(config.clinic_info.clinic_instagram || "");
          setClinicFacebook(config.clinic_info.clinic_facebook || "");
          setClinicSite(config.clinic_info.clinic_site || "");
          setClinicHistory(config.clinic_info.clinic_history || "");
        }

        if (config.team_and_specialties) {
          setTechnicalResponsible(config.team_and_specialties.technical_responsible || "");
          setTreatments(config.team_and_specialties.treatments || []);
        }

        if (config.scheduling_config) {
          setExistingSchedulingConfig(config.scheduling_config);
          setMonday(processScheduleDay(config.scheduling_config.monday));
          setTuesday(processScheduleDay(config.scheduling_config.tuesday));
          setWednesday(processScheduleDay(config.scheduling_config.wednesday));
          setThursday(processScheduleDay(config.scheduling_config.thursday));
          setFriday(processScheduleDay(config.scheduling_config.friday));
          setSaturday(processScheduleDay(config.scheduling_config.saturday));
          setSunday(processScheduleDay(config.scheduling_config.sunday));
          setConsultationDuration(config.scheduling_config.consultation_duration || 30);
          setNumberOfSuggestions(config.scheduling_config.number_of_suggestions || 3);
          setMinimumBookingNoticeMinutes(resolveMinimumBookingNoticeMinutes(
            config.scheduling_config.minimum_booking_notice_minutes
              ?? DEFAULT_MINIMUM_BOOKING_NOTICE_MINUTES,
          ));
          const savedUrgency = config.scheduling_config.urgency
            && typeof config.scheduling_config.urgency === 'object'
            ? config.scheduling_config.urgency : {};
          setUrgencyPolicy({
            ...EMPTY_URGENCY_POLICY,
            ...savedUrgency,
            version: 1,
            accepts_urgent_cases: typeof savedUrgency.accepts_urgent_cases === 'boolean'
              ? savedUrgency.accepts_urgent_cases : null,
            paid_separately: typeof savedUrgency.paid_separately === 'boolean'
              ? savedUrgency.paid_separately : null,
            approved_price_text: typeof savedUrgency.approved_price_text === 'string'
              ? savedUrgency.approved_price_text : null,
            same_day_fit: ['yes', 'no', 'not_configured'].includes(savedUrgency.same_day_fit)
              ? savedUrgency.same_day_fit : 'not_configured',
            on_call_windows: Array.isArray(savedUrgency.on_call_windows)
              ? savedUrgency.on_call_windows.map((window: UrgencyWindow) => ({ ...window })) : [],
            holiday_policy: typeof savedUrgency.holiday_policy === 'string'
              ? savedUrgency.holiday_policy : null,
          });
        }

        if (config.financial_config) {
          setAcceptsHealthInsurance(config.financial_config.accepts_health_insurance || false);
          setHealthInsurancePlans(config.financial_config.health_insurance_plans || "");
          setPaymentMethods((config.financial_config.payment_methods || []).join(","));
          setInstallmentConditions(config.financial_config.installment_conditions || "");
          setEvaluationPrice(config.financial_config.evaluation_price || "");
          setTreatmentPrices(config.financial_config.treatment_prices || "");
        }

        if (config.conversation_flow) {
          setStep0(config.conversation_flow.step0 || "");
          setStep1First(config.conversation_flow.step1First || "");
          setStep1Second(config.conversation_flow.step1Second || "");
          setStep2(config.conversation_flow.step2 || "");
          setStep3(config.conversation_flow.step3 || "");
          setMaxTokens(config.conversation_flow.max_tokens || 1000);

          setFinancialRedirectType(config.conversation_flow.financial_redirect?.type || "fixo");
          setFinancialRedirectNumber(config.conversation_flow.financial_redirect?.number || "");
          setRegularRedirectType(config.conversation_flow.regular_redirect?.type || "fixo");
          setRegularRedirectNumber(config.conversation_flow.regular_redirect?.number || "");
          setMaintenanceRedirectType(config.conversation_flow.maintenance_redirect?.type || "fixo");
          setMaintenanceRedirectNumber(config.conversation_flow.maintenance_redirect?.number || "");
          setActivePatientsRedirectType(config.conversation_flow.active_patients_redirect?.type || "fixo");
          setActivePatientsRedirectNumber(config.conversation_flow.active_patients_redirect?.number || "");
          setFewShots(config.conversation_flow.few_shots || []);
        }

        setHasConfig(true);
      } catch (error) {
        console.log("Nenhuma configuração encontrada ou erro ao obter config:", error.message);
        setHasConfig(false);
      } finally {
        setLoading(false);
      }
    };
    
    fetchConfig();
  }, []);

  // Construir payload para enviar ao servidor
  const buildPayload = () => {
    const payload: any = {
      assistant_identity: {
        assistant_name: assistantName,
        assistant_role: assistantRole,
        assistant_responsibility: assistantResponsibility,
        assistant_formality: assistantFormality,
        assistant_tone: assistantTone,
        assistant_language: assistantLanguage
      },
      clinic_info: {
        clinic_name: clinicName,
        clinic_location: clinicLocation,
        clinic_address: clinicAddress,
        clinic_phone_fixed: clinicPhoneFixed,
        clinic_whatsapp: clinicWhatsApp,
        clinic_maps: clinicMaps,
        clinic_instagram: clinicInstagram,
        clinic_facebook: clinicFacebook,
        clinic_site: clinicSite,
        clinic_history: clinicHistory
      },
      team_and_specialties: {
        technical_responsible: technicalResponsible,
        treatments: treatments
      },
      scheduling_config: mergeSchedulingConfig(existingSchedulingConfig, {
        monday,
        tuesday,
        wednesday,
        thursday,
        friday,
        saturday,
        sunday,
        consultation_duration: consultationDuration,
        number_of_suggestions: numberOfSuggestions,
        minimum_booking_notice_minutes: minimumBookingNoticeMinutes,
        urgency: {
          ...urgencyPolicy,
          approved_price_text: urgencyPolicy.approved_price_text?.trim() || null,
          holiday_policy: urgencyPolicy.holiday_policy?.trim() || null,
        },
      }),
      financial_config: {
        accepts_health_insurance: acceptsHealthInsurance,
        payment_methods: paymentMethods.split(",").map(pm => pm.trim()),
        installment_conditions: installmentConditions,
        health_insurance_plans: healthInsurancePlans,
        evaluation_price: evaluationPrice,
        treatment_prices: treatmentPrices
      },
      conversation_flow: {
        step0,
        step1First,
        step1Second,
        step2,
        step3,
        max_tokens: maxTokens,
        financial_redirect: { type: financialRedirectType, number: financialRedirectNumber },
        regular_redirect: { type: regularRedirectType, number: regularRedirectNumber },
        maintenance_redirect: { type: maintenanceRedirectType, number: maintenanceRedirectNumber },
        active_patients_redirect: { type: activePatientsRedirectType, number: activePatientsRedirectNumber },
        few_shots: fewShots
      }
    };

    if (!hasUnrecognizedVoiceConfig || agentVoiceTouched) {
      payload.assistant_tone_and_voice = {
        tts: agentVoice ? {
          voice_id: agentVoice.voice_id,
          voice_gender: agentVoice.voice_gender,
        } : null,
      };
    }

    return payload;
  };

  // Salvar nova configuração
  const handleSave = async () => {
    if (!agentVoiceValid) return;
    if (urgencyPolicy.on_call_windows.some((window) => !window.start || !window.end || window.start >= window.end)) {
      setUrgencyValidationError('Informe início e fim válidos, com o fim depois do início, ou remova a janela.');
      setExpandedSections((sections) => sections.includes('urgency') ? sections : [...sections, 'urgency']);
      return;
    }
    setSaveStatus('saving');
    const payload = buildPayload();
    try {
      const data = await saveAgentConfig(payload);
      console.log("Config salva com sucesso:", data);
      setSaveStatus('success');
      setHasConfig(true);
      setIsEditing(false);
      setPersistedAgentVoice(agentVoice);
      setPersistedAssistantName(assistantName);
      setAgentVoiceTouched(false);
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      console.error("Erro ao salvar config:", error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 2000);
    }
  };

  // Excluir configuração
  const handleDelete = async () => {
    if (window.confirm("Tem certeza que deseja excluir esta configuração?")) {
      try {
        await deleteAgentConfig();
        setHasConfig(false);
        setIsEditing(false);
        setAgentVoice(null);
        setPersistedAgentVoice(null);
        setPersistedAssistantName('');
        setHasUnrecognizedVoiceConfig(false);
        setAgentVoiceTouched(false);
      } catch (error) {
        console.error("Erro ao deletar config:", error);
        alert(error.message || "Erro ao deletar configurações!");
      }
    }
  };

  // Atualizar configuração existente
  const handleUpdate = async () => {
    if (!agentVoiceValid) return;
    if (urgencyPolicy.on_call_windows.some((window) => !window.start || !window.end || window.start >= window.end)) {
      setUrgencyValidationError('Informe início e fim válidos, com o fim depois do início, ou remova a janela.');
      setExpandedSections((sections) => sections.includes('urgency') ? sections : [...sections, 'urgency']);
      return;
    }
    setSaveStatus('saving');
    const payload = buildPayload();
    try {
      const data = await updateAgentConfig(payload);
      console.log("Config atualizada com sucesso:", data);
      setSaveStatus('success');
      setHasConfig(true);
      setIsEditing(false);
      setPersistedAgentVoice(agentVoice);
      setPersistedAssistantName(assistantName);
      setAgentVoiceTouched(false);
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      console.error("Erro ao atualizar config:", error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 2000);
    }
  };

  const updateUrgencyPolicy = (updates: Partial<UrgencyPolicyConfig>) => {
    setUrgencyPolicy((current) => ({ ...current, ...updates }));
    setUrgencyValidationError('');
  };

  const updateUrgencyWindow = (
    index: number,
    updates: Partial<UrgencyWindow>,
  ) => {
    updateUrgencyPolicy({
      on_call_windows: urgencyPolicy.on_call_windows.map((window, currentIndex) => (
        currentIndex === index ? { ...window, ...updates } : window
      )),
    });
  };

  // Componente para renderizar cada seção (funcionalidade original com visual da empresa)
  const SectionContainer = useMemo(() => React.memo(({ id, title, icon: Icon, children }: {
    id: string;
    title: string;
    icon: React.ElementType;
    children: React.ReactNode;
  }) => {
    const isExpanded = expandedSections.includes(id);
    
    return (
      <div className={`mb-4 rounded-2xl shadow-xl border overflow-hidden transition-all duration-200 ${
        isDark 
          ? 'bg-gray-800 border-gray-600' 
          : 'bg-white border-gray-200'
      }`}>
        <button 
          type="button"
          onClick={(e) => toggleSection(id, e)}
          className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
            isDark 
              ? 'hover:bg-gray-700/40'
              : 'hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl transition-colors ${
              isExpanded 
                ? 'bg-sminy-primary/20 text-sminy-primary'
                : isDark 
                  ? 'bg-gray-700 text-gray-400'
                  : 'bg-gray-100 text-gray-600'
            }`}>
              <Icon className="h-5 w-5" />
            </div>
            <span className={`font-semibold ${
              isDark ? 'text-gray-200' : 'text-gray-800'
            }`}>{title}</span>
          </div>
          {isExpanded ? 
            <ChevronDown className={`h-5 w-5 ${
              isDark ? 'text-gray-400' : 'text-gray-400'
            }`} /> : 
            <ChevronRight className={`h-5 w-5 ${
              isDark ? 'text-gray-400' : 'text-gray-400'
            }`} />
          }
        </button>
        
        {isExpanded && (
          <div className={`p-4 border-t ${
            isDark 
              ? 'border-gray-600 bg-gray-800/50'
              : 'border-gray-200 bg-gray-50/50'
          }`}>
            {children}
          </div>
        )}
      </div>
    );
  }), [expandedSections, isDark, toggleSection]);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDark ? 'bg-[#111827]' : 'bg-gradient-to-b from-gray-50 to-white'
      }`}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-sminy-primary/30 border-t-sminy-primary rounded-full animate-spin mx-auto"></div>
          <p className={`mt-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen w-full ${
      isDark 
        ? 'bg-[#111827] text-gray-200' 
        : 'bg-gradient-to-b from-gray-50 to-white text-gray-800'
    }`}>
      {/* Header */}
      <header className={`sticky top-0 z-20 border-b px-4 py-3 shadow-sm backdrop-blur ${
        isDark 
          ? 'border-gray-700 bg-[#111827]/90'
          : 'border-gray-200 bg-white/90'
      }`}>
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
          <div>
            <h1 className={`text-lg font-semibold tracking-tight ${
              isDark ? 'text-white' : 'text-gray-800'
            }`}>Configuração do Agente</h1>
            <p className={`text-xs ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`}>Personalize como seu agente de inteligência artificial interage com o paciente.</p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className={`rounded-full px-3 py-1 ${statusColor}`}>
              {hasConfig ? "Ativo" : "Rascunho"}
            </span>
            <div className="flex items-center gap-2">
              {/* Se tem config e NÃO está editando: Editar + Excluir */}
              {hasConfig && !isEditing ? (
                <>
                  <button 
                    onClick={() => {
                      const currentScrollY = window.scrollY;
                      setIsEditing(true);
                      requestAnimationFrame(() => window.scrollTo(0, currentScrollY));
                    }}
                    className={`rounded-xl border px-3 py-2 transition-colors ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Edit2 className="mr-1 inline h-4 w-4"/>Editar
                  </button>
                  <button 
                    onClick={() => setShowDeleteModal(true)}
                    className="rounded-xl bg-red-500 px-3 py-2 text-white hover:bg-red-600 transition-colors"
                  >
                    <Trash2 className="mr-1 inline h-4 w-4"/>Excluir
                  </button>
                </>
              ) : (
                /* Modo edição ou sem config: apenas Salvar */
                <button 
                  onClick={hasConfig ? handleUpdate : handleSave}
                  disabled={saveStatus === 'saving' || !agentVoiceValid}
                  title={!agentVoiceValid ? 'Escolha uma voz disponível antes de salvar.' : undefined}
                  className="rounded-xl bg-sminy-primary px-3 py-2 text-white hover:bg-sminy-primary/90 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Save className="mr-1 inline h-4 w-4"/>Salvar
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="mx-auto mt-2 max-w-6xl">
          <div className={`relative h-2 w-full overflow-hidden rounded-full ${
            isDark ? 'bg-gray-700' : 'bg-gray-100'
          }`}>
            <div className="absolute left-0 top-0 h-2 rounded-full bg-sminy-primary" style={{ width: `${pct}%` }} />
          </div>
          <p className={`mt-1 text-right text-[11px] ${
            isDark ? 'text-gray-400' : 'text-gray-500'
          }`}>Progresso: {pct}%</p>
          {!agentVoiceValid && (!hasConfig || isEditing) && (
            <p role="alert" className={`mt-1 text-right text-xs font-medium ${
              isDark ? 'text-amber-300' : 'text-amber-700'
            }`}>
              Escolha uma voz disponível antes de salvar.
            </p>
          )}
        </div>
      </header>

      <div className="mx-auto max-w-6xl p-4">

        {/* Conteúdo principal - Formulários em Seções com visual moderno */}
        {!hasConfig || isEditing ? (
          <>
            {/* Seção de Identidade do Agente */}
            <SectionContainer id="identity" title="Identidade do Agente" icon={Bot}>
              <AssistantIdentityForm
                assistantName={assistantName} setAssistantName={setAssistantName}
                assistantRole={assistantRole} setAssistantRole={setAssistantRole}
                assistantResponsibility={assistantResponsibility} setAssistantResponsibility={setAssistantResponsibility}
                assistantFormality={assistantFormality} setAssistantFormality={setAssistantFormality}
                assistantTone={assistantTone} setAssistantTone={setAssistantTone}
                assistantLanguage={assistantLanguage} setAssistantLanguage={setAssistantLanguage}
              />
              <div className={`my-5 border-t ${isDark ? 'border-gray-600' : 'border-gray-200'}`} />
              <AgentVoiceSelector
                assistantName={assistantName}
                value={agentVoice}
                persistedAssistantName={persistedAssistantName}
                persistedValue={persistedAgentVoice}
                onChange={handleAgentVoiceChange}
                onValidityChange={setAgentVoiceValid}
              />
            </SectionContainer>

            {/* Seção de Informações da Clínica */}
            <SectionContainer id="clinic" title="Informações da Clínica" icon={Building}>
              <ClinicInfoForm
                clinicName={clinicName} setClinicName={setClinicName}
                clinicLocation={clinicLocation} setClinicLocation={setClinicLocation}
                clinicAddress={clinicAddress} setClinicAddress={setClinicAddress}
                clinicPhoneFixed={clinicPhoneFixed} setClinicPhoneFixed={setClinicPhoneFixed}
                clinicWhatsApp={clinicWhatsApp} setClinicWhatsApp={setClinicWhatsApp}
                clinicMaps={clinicMaps} setClinicMaps={setClinicMaps}
                clinicInstagram={clinicInstagram} setClinicInstagram={setClinicInstagram}
                clinicFacebook={clinicFacebook} setClinicFacebook={setClinicFacebook}
                clinicSite={clinicSite} setClinicSite={setClinicSite}
                clinicHistory={clinicHistory} setClinicHistory={setClinicHistory}
              />
            </SectionContainer>

            {/* Seção de Equipe e Especialidades */}
            <SectionContainer id="team" title="Equipe e Especialidades" icon={Users}>
              <TeamAndSpecialtiesForm
                technicalResponsible={technicalResponsible}
                setTechnicalResponsible={setTechnicalResponsible}
                treatments={treatments}
                setTreatments={setTreatments as SetTreatmentsFunction}
              />
            </SectionContainer>

            {/* Seção de Configurações de Agendamento */}
            <SectionContainer id="scheduling" title="Configuração de Agendamento" icon={Calendar}>
              <SchedulingConfigForm
                monday={monday} setMonday={setMonday}
                tuesday={tuesday} setTuesday={setTuesday}
                wednesday={wednesday} setWednesday={setWednesday}
                thursday={thursday} setThursday={setThursday}
                friday={friday} setFriday={setFriday}
                saturday={saturday} setSaturday={setSaturday}
                sunday={sunday} setSunday={setSunday}
                consultationDuration={consultationDuration}
                setConsultationDuration={setConsultationDuration}
                numberOfSuggestions={numberOfSuggestions}
                setNumberOfSuggestions={setNumberOfSuggestions}
                minimumBookingNoticeMinutes={minimumBookingNoticeMinutes}
                setMinimumBookingNoticeMinutes={setMinimumBookingNoticeMinutes}
              />
            </SectionContainer>

            <SectionContainer id="urgency" title="Urgência" icon={CircleAlert}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="block text-sm">
                  <span className={`mb-1 block font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    A clínica aceita casos urgentes?
                  </span>
                  <select
                    value={urgencyPolicy.accepts_urgent_cases === true ? 'yes' : urgencyPolicy.accepts_urgent_cases === false ? 'no' : 'unknown'}
                    onChange={(event) => updateUrgencyPolicy({
                      accepts_urgent_cases: event.target.value === 'yes' ? true : event.target.value === 'no' ? false : null,
                    })}
                    className={`w-full rounded-lg border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-300 bg-white text-gray-800'}`}
                  >
                    <option value="unknown">Não informado</option>
                    <option value="yes">Sim</option>
                    <option value="no">Não</option>
                  </select>
                </label>
                <label className="block text-sm">
                  <span className={`mb-1 block font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Há cobrança separada para urgência?
                  </span>
                  <select
                    value={urgencyPolicy.paid_separately === true ? 'yes' : urgencyPolicy.paid_separately === false ? 'no' : 'unknown'}
                    onChange={(event) => updateUrgencyPolicy({
                      paid_separately: event.target.value === 'yes' ? true : event.target.value === 'no' ? false : null,
                    })}
                    className={`w-full rounded-lg border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-300 bg-white text-gray-800'}`}
                  >
                    <option value="unknown">Não informado</option>
                    <option value="yes">Sim</option>
                    <option value="no">Não</option>
                  </select>
                </label>
                <label className="block text-sm md:col-span-2">
                  <span className={`mb-1 block font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Texto de preço aprovado para informar
                  </span>
                  <input
                    type="text"
                    maxLength={240}
                    value={urgencyPolicy.approved_price_text || ''}
                    onChange={(event) => updateUrgencyPolicy({ approved_price_text: event.target.value || null })}
                    className={`w-full rounded-lg border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-300 bg-white text-gray-800'}`}
                  />
                </label>
                <label className="block text-sm md:col-span-2">
                  <span className={`mb-1 block font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Possibilidade de encaixe no mesmo dia
                  </span>
                  <select
                    value={urgencyPolicy.same_day_fit}
                    onChange={(event) => updateUrgencyPolicy({ same_day_fit: event.target.value as UrgencyPolicyConfig['same_day_fit'] })}
                    className={`w-full rounded-lg border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-300 bg-white text-gray-800'}`}
                  >
                    <option value="not_configured">Não informado</option>
                    <option value="yes">Pode ser avaliada pela equipe</option>
                    <option value="no">Não oferece encaixe no mesmo dia</option>
                  </select>
                </label>
              </div>

              <div className="mt-5">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h3 className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Janelas de plantão informadas
                  </h3>
                  <button
                    type="button"
                    onClick={() => updateUrgencyPolicy({
                      on_call_windows: [...urgencyPolicy.on_call_windows, { weekday: 'monday', start: '', end: '' }],
                    })}
                    className={`rounded-lg border px-3 py-1.5 text-xs ${isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                  >
                    Adicionar janela
                  </button>
                </div>
                {urgencyPolicy.on_call_windows.length === 0 && (
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Nenhuma janela configurada.
                  </p>
                )}
                <div className="space-y-2">
                  {urgencyPolicy.on_call_windows.map((window, index) => (
                    <div key={`urgency-window-${index}`} className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
                      <label className="block text-xs">
                        <span className={`mb-1 block ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Dia</span>
                        <select
                          value={window.weekday}
                          onChange={(event) => updateUrgencyWindow(index, { weekday: event.target.value })}
                          className={`w-full rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-300 bg-white text-gray-800'}`}
                        >
                          <option value="monday">Segunda-feira</option>
                          <option value="tuesday">Terça-feira</option>
                          <option value="wednesday">Quarta-feira</option>
                          <option value="thursday">Quinta-feira</option>
                          <option value="friday">Sexta-feira</option>
                          <option value="saturday">Sábado</option>
                          <option value="sunday">Domingo</option>
                        </select>
                      </label>
                      <label className="block text-xs">
                        <span className={`mb-1 block ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Início</span>
                        <input type="time" value={window.start} onChange={(event) => updateUrgencyWindow(index, { start: event.target.value })}
                          className={`w-full rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-300 bg-white text-gray-800'}`} />
                      </label>
                      <label className="block text-xs">
                        <span className={`mb-1 block ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Fim</span>
                        <input type="time" value={window.end} onChange={(event) => updateUrgencyWindow(index, { end: event.target.value })}
                          className={`w-full rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-300 bg-white text-gray-800'}`} />
                      </label>
                      <button type="button" aria-label="Remover janela de plantão"
                        onClick={() => updateUrgencyPolicy({ on_call_windows: urgencyPolicy.on_call_windows.filter((_, currentIndex) => currentIndex !== index) })}
                        className={`rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}>
                        Remover
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <label className="mt-5 block text-sm">
                <span className={`mb-1 block font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Política para feriados
                </span>
                <textarea
                  rows={3}
                  maxLength={1000}
                  value={urgencyPolicy.holiday_policy || ''}
                  onChange={(event) => updateUrgencyPolicy({ holiday_policy: event.target.value || null })}
                  className={`w-full rounded-lg border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-300 bg-white text-gray-800'}`}
                />
              </label>
              {urgencyValidationError && (
                <p role="alert" className={`mt-2 text-sm ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                  {urgencyValidationError}
                </p>
              )}
            </SectionContainer>

            {/* Seção de Informações Financeiras */}
            <SectionContainer id="financial" title="Informações Financeiras" icon={DollarSign}>
              <FinancialConfigForm
                acceptsHealthInsurance={acceptsHealthInsurance}
                setAcceptsHealthInsurance={setAcceptsHealthInsurance}
                healthInsurancePlans={healthInsurancePlans}
                setHealthInsurancePlans={setHealthInsurancePlans}
                paymentMethods={paymentMethods}
                setPaymentMethods={setPaymentMethods}
                installmentConditions={installmentConditions}
                setInstallmentConditions={setInstallmentConditions}
                evaluationPrice={evaluationPrice}
                setEvaluationPrice={setEvaluationPrice}
                treatmentPrices={treatmentPrices}
                setTreatmentPrices={setTreatmentPrices}
              />
            </SectionContainer>

            {/* Seção de Fluxo de Conversa */}
            <SectionContainer id="conversation" title="Fluxo de Conversa" icon={MessageSquare}>
              <ConversationFlowForm
                step0={step0} setStep0={setStep0}
                step1First={step1First} setStep1First={setStep1First}
                step1Second={step1Second} setStep1Second={setStep1Second}
                step2={step2} setStep2={setStep2}
                step3={step3} setStep3={setStep3}
                maxTokens={maxTokens} setMaxTokens={setMaxTokens}
                financialRedirectType={financialRedirectType}
                setFinancialRedirectType={setFinancialRedirectType}
                financialRedirectNumber={financialRedirectNumber}
                setFinancialRedirectNumber={setFinancialRedirectNumber}
                regularRedirectType={regularRedirectType}
                setRegularRedirectType={setRegularRedirectType}
                regularRedirectNumber={regularRedirectNumber}
                setRegularRedirectNumber={setRegularRedirectNumber}
                maintenanceRedirectType={maintenanceRedirectType}
                setMaintenanceRedirectType={setMaintenanceRedirectType}
                maintenanceRedirectNumber={maintenanceRedirectNumber}
                setMaintenanceRedirectNumber={setMaintenanceRedirectNumber}
                activePatientsRedirectType={activePatientsRedirectType}
                setActivePatientsRedirectType={setActivePatientsRedirectType}
                activePatientsRedirectNumber={activePatientsRedirectNumber}
                setActivePatientsRedirectNumber={setActivePatientsRedirectNumber}
                fewShots={fewShots}
                setFewShots={setFewShots as SetFewShotsFunction}
              />
            </SectionContainer>
          </>
        ) : (
          <div className="space-y-4">
            {/* Banner com informação principal */}
            <div className={`rounded-2xl border p-4 ${
              isDark 
                ? 'border-sminy-primary/30 bg-sminy-primary/10'
                : 'border-sminy-primary/20 bg-sminy-primary/5'
            }`}>
              <div className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-sminy-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-sminy-primary mb-1">Configuração atual</h4>
                  <p className={`text-sm ${
                    isDark ? 'text-sminy-primary/90' : 'text-sminy-primary/80'
                  }`}>
                    Seus dados estão prontos para uso. Você pode editar qualquer seção individualmente.
                  </p>
                </div>
              </div>
            </div>

            {/* Cards de configuração detalhada (seguindo exemplo) */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Identidade do Agente */}
              <div className={`rounded-2xl border p-4 shadow-xl ${
                isDark 
                  ? 'border-gray-600 bg-gray-800'
                  : 'border-gray-200 bg-white'
              }`}>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className={`text-sm font-semibold ${
                    isDark ? 'text-gray-200' : 'text-gray-800'
                  }`}>Identidade do Agente</h3>
                  <button 
                    onClick={() => {
                      const currentScrollY = window.scrollY;
                      setExpandedSections(['identity']);
                      setIsEditing(true);
                      requestAnimationFrame(() => window.scrollTo(0, currentScrollY));
                    }}
                    className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                      isDark 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Editar
                  </button>
                </div>
                <ul className={`text-sm ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <li><strong>Nome:</strong> {assistantName || '—'}</li>
                  <li><strong>Função:</strong> {assistantRole || '—'}</li>
                  <li><strong>Tom:</strong> {assistantTone || '—'}</li>
                  <li><strong>Voz:</strong> {agentVoice
                    ? `${agentVoice.voice_name} — ${agentVoice.voice_gender === 'female' ? 'Feminina' : 'Masculina'}`
                    : 'Não selecionada'}</li>
                  <li><strong>Responsabilidade:</strong> {assistantResponsibility || '—'}</li>
                </ul>
              </div>

              {/* Informações da Clínica */}
              <div className={`rounded-2xl border p-4 shadow-xl ${
                isDark 
                  ? 'border-gray-600 bg-gray-800'
                  : 'border-gray-200 bg-white'
              }`}>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className={`text-sm font-semibold ${
                    isDark ? 'text-gray-200' : 'text-gray-800'
                  }`}>Informações da Clínica</h3>
                  <button 
                    onClick={() => {
                      const currentScrollY = window.scrollY;
                      setExpandedSections(['clinic']);
                      setIsEditing(true);
                      requestAnimationFrame(() => window.scrollTo(0, currentScrollY));
                    }}
                    className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                      isDark 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Editar
                  </button>
                </div>
                <ul className={`text-sm ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <li><strong>Nome:</strong> {clinicName || '—'}</li>
                  <li><strong>Localização:</strong> {clinicLocation || '—'}</li>
                  <li><strong>Telefone:</strong> {clinicPhoneFixed || '—'}</li>
                  <li><strong>WhatsApp:</strong> {clinicWhatsApp || '—'}</li>
                </ul>
              </div>

              {/* Equipe e Especialidades */}
              <div className={`rounded-2xl border p-4 shadow-xl ${
                isDark 
                  ? 'border-gray-600 bg-gray-800'
                  : 'border-gray-200 bg-white'
              }`}>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className={`text-sm font-semibold ${
                    isDark ? 'text-gray-200' : 'text-gray-800'
                  }`}>Equipe e Especialidades</h3>
                  <button 
                    onClick={() => {
                      const currentScrollY = window.scrollY;
                      setExpandedSections(['team']);
                      setIsEditing(true);
                      requestAnimationFrame(() => window.scrollTo(0, currentScrollY));
                    }}
                    className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                      isDark 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Editar
                  </button>
                </div>
                <ul className={`text-sm ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <li><strong>Responsável:</strong> {technicalResponsible || '—'}</li>
                  <li><strong>Tratamentos:</strong> {treatments.length ? `${treatments.length} configurados` : '—'}</li>
                </ul>
              </div>

              {/* Agendamento */}
              <div className={`rounded-2xl border p-4 shadow-xl ${
                isDark 
                  ? 'border-gray-600 bg-gray-800'
                  : 'border-gray-200 bg-white'
              }`}>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className={`text-sm font-semibold ${
                    isDark ? 'text-gray-200' : 'text-gray-800'
                  }`}>Agendamento</h3>
                  <button 
                    onClick={() => {
                      const currentScrollY = window.scrollY;
                      setExpandedSections(['scheduling']);
                      setIsEditing(true);
                      requestAnimationFrame(() => window.scrollTo(0, currentScrollY));
                    }}
                    className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                      isDark 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Editar
                  </button>
                </div>
                <ul className={`text-sm ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <li><strong>Dias ativos:</strong> {[
                    monday.morningEnabled || monday.afternoonEnabled ? 'Seg' : null,
                    tuesday.morningEnabled || tuesday.afternoonEnabled ? 'Ter' : null,
                    wednesday.morningEnabled || wednesday.afternoonEnabled ? 'Qua' : null,
                    thursday.morningEnabled || thursday.afternoonEnabled ? 'Qui' : null,
                    friday.morningEnabled || friday.afternoonEnabled ? 'Sex' : null,
                    saturday.morningEnabled || saturday.afternoonEnabled ? 'Sáb' : null,
                    sunday.morningEnabled || sunday.afternoonEnabled ? 'Dom' : null
                  ].filter(Boolean).join(', ') || '—'}</li>
                  <li><strong>Duração:</strong> {consultationDuration} min</li>
                  <li><strong>Antecedência do agente:</strong> {minimumBookingNoticeMinutes} min</li>
                  <li><strong>Sugestões:</strong> {numberOfSuggestions} opções</li>
                </ul>
              </div>

              {/* Informações Financeiras */}
              <div className={`rounded-2xl border p-4 shadow-xl ${
                isDark 
                  ? 'border-gray-600 bg-gray-800'
                  : 'border-gray-200 bg-white'
              }`}>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className={`text-sm font-semibold ${
                    isDark ? 'text-gray-200' : 'text-gray-800'
                  }`}>Informações Financeiras</h3>
                  <button 
                    onClick={() => {
                      const currentScrollY = window.scrollY;
                      setExpandedSections(['financial']);
                      setIsEditing(true);
                      requestAnimationFrame(() => window.scrollTo(0, currentScrollY));
                    }}
                    className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                      isDark 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Editar
                  </button>
                </div>
                <ul className={`text-sm ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <li><strong>Convênios:</strong> {acceptsHealthInsurance ? 'Aceita' : 'Não aceita'}</li>
                  <li><strong>Preço avaliação:</strong> {evaluationPrice ? 'Configurado' : '—'}</li>
                </ul>
              </div>

              {/* Fluxo de Conversa */}
              <div className={`rounded-2xl border p-4 shadow-xl ${
                isDark 
                  ? 'border-gray-600 bg-gray-800'
                  : 'border-gray-200 bg-white'
              }`}>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className={`text-sm font-semibold ${
                    isDark ? 'text-gray-200' : 'text-gray-800'
                  }`}>Fluxo de Conversa</h3>
                  <button 
                    onClick={() => {
                      const currentScrollY = window.scrollY;
                      setExpandedSections(['conversation']);
                      setIsEditing(true);
                      requestAnimationFrame(() => window.scrollTo(0, currentScrollY));
                    }}
                    className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                      isDark 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Editar
                  </button>
                </div>
                <div className={`text-sm ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <p className="line-clamp-2"><strong>Saudação:</strong> {step0 || '—'}</p>
                  <p className="mt-1"><strong>Max tokens:</strong> {maxTokens}</p>
                  <p className="mt-1"><strong>Few-shots:</strong> {fewShots.length} exemplos</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Modal de confirmação de exclusão */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Excluir configuração do agente"
        message="Tem certeza que deseja excluir toda a configuração do agente? Esta ação não pode ser desfeita e você precisará configurar tudo novamente."
        confirmText="Sim, excluir"
        cancelText="Cancelar"
      />
    </div>
  );
};

export default PromptConfigRebranded;
